import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Play, ArrowLeft, ChevronRight } from 'lucide-react';
import { SEO } from '../components/SEO';
import { imgUrl } from '../utils/imgUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOMAIN = 'https://rezvanifurniturestudio.com';

const SECTIONS = {
  'product-videos': 'Product Videos',
  'instructional-videos': 'Instructional Videos',
};

let _videosCache = null;
const fetchVideos = async () => {
  if (_videosCache) return _videosCache;
  const res = await axios.get(`${API}/videos`);
  _videosCache = res.data;
  return _videosCache;
};

const videoThumb = (v) =>
  v.youtubeId ? `https://img.youtube.com/vi/${v.youtubeId}/hqdefault.jpg` : `${DOMAIN}/og-default.jpg`;

const VideoCard = ({ v }) => (
  <Link
    to={`/videos/${v.section}/${v.slug}`}
    data-testid={`video-card-${v.slug}`}
    className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 text-left block"
  >
    <div className="relative aspect-video overflow-hidden bg-gray-900">
      {v.youtubeId ? (
        <img
          src={`https://img.youtube.com/vi/${v.youtubeId}/hqdefault.jpg`}
          alt={v.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      ) : (
        <video
          src={imgUrl(v.src)}
          preload="metadata"
          muted
          playsInline
          className="w-full h-full object-contain bg-black transition-transform duration-700 group-hover:scale-105"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent flex items-center justify-center transition-all duration-300 group-hover:from-black/60">
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl transform transition-transform duration-300 group-hover:scale-110">
          <Play size={32} className="text-cyan-600 ml-1" fill="currentColor" />
        </div>
      </div>
    </div>
    <div className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-cyan-600 transition-colors duration-300 line-clamp-2">
        {v.title}
      </h3>
      <p className="text-sm text-gray-600 line-clamp-2">{v.description}</p>
    </div>
  </Link>
);

const Breadcrumbs = ({ trail }) => (
  <nav aria-label="Breadcrumb" data-testid="video-breadcrumbs" className="mb-6">
    <ol className="flex flex-wrap items-center gap-1.5 text-sm text-gray-500">
      {trail.map((item, i) => (
        <li key={i} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight size={14} className="text-gray-400" />}
          {item.to ? (
            <Link to={item.to} className="hover:text-cyan-600 transition-colors">{item.label}</Link>
          ) : (
            <span className="text-gray-800 font-medium line-clamp-1">{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

const VideoDetail = ({ video, related }) => {
  const playerRef = useRef(null);
  const sectionLabel = SECTIONS[video.section];
  const pageUrl = `${DOMAIN}/videos/${video.section}/${video.slug}`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: video.title,
      description: video.description,
      thumbnailUrl: [videoThumb(video)],
      uploadDate: video.uploadDate,
      url: pageUrl,
      ...(video.youtubeId
        ? { embedUrl: `https://www.youtube-nocookie.com/embed/${video.youtubeId}` }
        : { contentUrl: video.src }),
      publisher: { '@type': 'Organization', name: 'Rezvani Furniture Studio', url: DOMAIN },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${DOMAIN}/` },
        { '@type': 'ListItem', position: 2, name: 'Videos', item: `${DOMAIN}/videos` },
        { '@type': 'ListItem', position: 3, name: sectionLabel, item: `${DOMAIN}/videos#${video.section}` },
        { '@type': 'ListItem', position: 4, name: video.title, item: pageUrl },
      ],
    },
  ];

  return (
    <div className="max-w-5xl mx-auto" data-testid={`video-page-${video.slug}`}>
      <SEO title={video.title} description={video.description} canonical={pageUrl} type="video.other" jsonLd={jsonLd} />
      <Breadcrumbs
        trail={[
          { label: 'Home', to: '/' },
          { label: 'Videos', to: '/videos' },
          { label: sectionLabel, to: `/videos#${video.section}` },
          { label: video.title },
        ]}
      />
      <div className="aspect-video w-full bg-black rounded-2xl overflow-hidden shadow-2xl">
        {video.youtubeId ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&playsinline=1`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="w-full h-full border-0"
            data-testid="video-player-iframe"
          />
        ) : (
          <video
            ref={playerRef}
            src={imgUrl(video.src)}
            controls
            playsInline
            preload="metadata"
            className="w-full h-full"
            data-testid="video-player"
          />
        )}
      </div>
      <div className="mt-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{video.title}</h1>
        <p className="text-base text-gray-600 mt-2">{video.description}</p>
        <Link
          to="/videos"
          data-testid="video-back-link"
          className="inline-flex items-center gap-1.5 mt-5 text-sm font-medium text-cyan-600 hover:text-cyan-700"
        >
          <ArrowLeft size={16} /> All Videos
        </Link>
      </div>

      {related.length > 0 && (
        <div className="mt-12">
          <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-5 pb-3 border-b border-gray-200">
            More {sectionLabel}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.slice(0, 3).map((v) => (
              <VideoCard key={v.slug} v={v} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Videos = () => {
  const { section, slug } = useParams();
  const [videos, setVideos] = useState(_videosCache || []);
  const [loaded, setLoaded] = useState(!!_videosCache);

  useEffect(() => {
    let alive = true;
    fetchVideos()
      .then((v) => { if (alive) { setVideos(v); setLoaded(true); } })
      .catch(() => setLoaded(true));
    return () => { alive = false; };
  }, []);

  // Scroll to a section when arriving with a #hash (e.g. from breadcrumbs)
  useEffect(() => {
    if (!slug && loaded && window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [slug, loaded]);

  if (slug) {
    if (!loaded) return <div className="min-h-screen pt-32 text-center text-gray-500">Loading…</div>;
    const video = videos.find((v) => v.section === section && v.slug === slug);
    if (!video) return <Navigate to="/videos" replace />;
    const related = videos.filter((v) => v.section === section && v.slug !== slug);
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 sm:pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <VideoDetail video={video} related={related} />
      </div>
    );
  }

  const productVideos = videos.filter((v) => v.section === 'product-videos');
  const instructionalVideos = videos.filter((v) => v.section === 'instructional-videos');

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-20">
      <SEO
        title="Product Videos | Rezvani Furniture Studio"
        description="Watch in-pool product videos featuring the Tenjam Freelo, Moon Chair, and more — see Rezvani Furniture Studio's premium pool furniture in action."
        canonical={`${DOMAIN}/videos`}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="text-sm font-semibold text-cyan-600 bg-cyan-50 px-4 py-2 rounded-full uppercase tracking-wider">
            Watch &amp; Discover
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mt-6 mb-4">Videos</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            See Tenjam in-pool furniture in action and learn how to care for it. Every video has its own page — click one to watch.
          </p>
        </div>

        <section className="mb-16" id="product-videos" data-testid="product-videos-section">
          <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-6 pb-3 border-b border-gray-200">Product Videos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="product-videos-grid">
            {productVideos.map((v) => (
              <VideoCard key={v.slug} v={v} />
            ))}
          </div>
        </section>

        <section id="instructional-videos" data-testid="instructional-videos-section">
          <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-6 pb-3 border-b border-gray-200">Instructional Videos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="instructional-videos-grid">
            {instructionalVideos.map((v) => (
              <VideoCard key={v.slug} v={v} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Videos;
